import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios from "../../api/axios";
import PublicPageLayout from "./PublicPageLayout";

// Inline styles for subcategory cards
const styles = {
    card: {
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        overflow: 'hidden'
    },
    cardHover: {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(17, 45, 78, 0.15)',
        borderColor: '#112d4e'
    },
    cardBody: {
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '140px',
        textAlign: 'center'
    },
    cardTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        color: '#112d4e',
        marginBottom: '8px'
    },
    cardSubtitle: {
        fontSize: '0.85rem',
        color: '#6c757d'
    },
    viewButton: {
        marginTop: '12px',
        padding: '6px 16px',
        fontSize: '0.8rem',
        backgroundColor: '#112d4e',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        opacity: 0,
        transition: 'opacity 0.2s ease'
    },
    viewButtonVisible: {
        opacity: 1
    }
};

/**
 * Public Subcategories Page
 * Displays subcategories for a given category
 * Uses PublicPageLayout for consistent header/footer
 */
function PublicHomeSubcategories() {
    const { categoryId, panelId } = useParams();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoryName, setCategoryName] = useState("");
    const [hoveredCard, setHoveredCard] = useState(null);

    useEffect(() => {
        if (!categoryId) return;
        const loadSubcats = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`/Subcategories/Public/ByCategory/${categoryId}`);
                if (typeof res.data === "string" && res.data === "No records") {
                    setSubcategories([]);
                } else {
                    setSubcategories(res.data);
                    if (res.data.length > 0) {
                        setCategoryName(res.data[0].categoryName);
                    }
                }
            } catch (err) {
                enqueueSnackbar(t("cannotLoadSubcategories") || "Cannot load subcategories", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        loadSubcats();
    }, [categoryId, enqueueSnackbar, t]);

    const handleSubcatClick = (subcategoryId) => {
        navigate(`/Tools/${panelId}/${categoryId}/${subcategoryId}`);
    };

    return (
        <PublicPageLayout>
            <div className="container px-3">
                {/* Breadcrumb */}
                <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/Home" style={{ color: '#112d4e' }}>
                                <i className="bi bi-house-door me-1"></i>
                                {t("home")}
                            </Link>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">
                            {categoryName || t("subcategories")}
                        </li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="card mb-4">
                    <div
                        className="card-header"
                        style={{
                            backgroundColor: '#112d4e',
                            color: '#fff',
                            padding: '20px',
                            textAlign: 'center'
                        }}
                    >
                        <h4 className="mb-0">{t("selectSubcategory")}</h4>
                        {categoryName && (
                            <p className="mb-0 mt-2" style={{ opacity: 0.9 }}>
                                {t("category")}: {categoryName}
                            </p>
                        )}
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" role="status" style={{ color: '#112d4e' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3" style={{ color: '#112d4e' }}>{t("loading")}...</p>
                    </div>
                ) : subcategories.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="text-muted">{t("noSubcategoriesFound")}</p>
                        <button
                            className="btn mt-2"
                            style={{ backgroundColor: '#112d4e', color: '#fff' }}
                            onClick={() => navigate('/Home')}
                        >
                            {t("backToHome")}
                        </button>
                    </div>
                ) : (
                    <div className="row g-4">
                        {subcategories.map((sc) => (
                            <div key={sc.id} className="col-12 col-sm-6 col-md-4">
                                <div
                                    style={{
                                        ...styles.card,
                                        ...(hoveredCard === sc.id ? styles.cardHover : {})
                                    }}
                                    onClick={() => handleSubcatClick(sc.id)}
                                    onMouseEnter={() => setHoveredCard(sc.id)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                >
                                    <div style={styles.cardBody}>
                                        <div style={styles.cardTitle}>{sc.name}</div>
                                        <div style={styles.cardSubtitle}>{sc.categoryName}</div>
                                        <button
                                            style={{
                                                ...styles.viewButton,
                                                ...(hoveredCard === sc.id ? styles.viewButtonVisible : {})
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSubcatClick(sc.id);
                                            }}
                                        >
                                            <i className="bi bi-arrow-right me-1"></i>
                                            {t("viewTools")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Back Button */}
                <div className="text-center mt-4">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/Home')}
                        style={{ borderColor: '#112d4e', color: '#112d4e' }}
                    >
                        <i className="bi bi-arrow-left me-2"></i>
                        {t("backToHome")}
                    </button>
                </div>
            </div>
        </PublicPageLayout>
    );
}

export default PublicHomeSubcategories;
